import React from 'react';
import { Lock, MessageSquare, Siren, KeyRound } from 'lucide-react';
import PageHeader from '../ui/PageHeader';
import Button from '../ui/Button';
import { CONTENT_WIDTH, PAGE_STACK, TEXT, ICON_TILE, SURFACE } from '../../uiTokens';

const USE_CASES = [
  {
    Icon: MessageSquare,
    title: 'A secret in Slack or email',
    problem: 'The secret sits in chat history and search forever, on every device that synced it.',
    solution: 'Send a one-time link instead. After they open it, the link is dead and there is nothing left to find.',
  },
  {
    Icon: Siren,
    title: 'On-call or break-glass access',
    problem: 'Incident credentials get pasted into tickets and threads under time pressure.',
    solution: 'Share a time-limited link. Get an email when it is opened, without ever storing the secret.',
  },
  {
    Icon: KeyRound,
    title: 'A key for a vendor or partner',
    problem: 'API keys in email attachments live in two inboxes and every backup of both.',
    solution: 'Send an encrypted link. Only someone with the complete URL can reveal it, once.',
  },
];

export default function UseCasesPage({ onNavigateHome }) {
  return (
    <div className={`${CONTENT_WIDTH} ${PAGE_STACK}`}>
      <PageHeader
        eyebrow="Use cases"
        title="When a copy left behind is the problem"
        description="Hand someone a secret without it living on in chat history, inboxes, or backups."
      />

      <ol className="mt-12 sm:mt-16 border-t border-line">
        {USE_CASES.map(({ Icon, title, problem, solution }) => (
          <li
            key={title}
            className="grid grid-cols-1 md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-4 md:gap-12 py-8 sm:py-10 border-b border-line"
          >
            <div className="flex items-start gap-3.5">
              <span className={ICON_TILE}>
                <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
              </span>
              <h2 className={`${TEXT.h2} pt-1`}>{title}</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-8">
              <div>
                <p className={`${TEXT.eyebrow} mb-2`}>The problem</p>
                <p className={TEXT.body}>{problem}</p>
              </div>
              <div>
                <p className={`${TEXT.eyebrowAccent} mb-2`}>With Hushify</p>
                <p className={TEXT.bodyStrong}>{solution}</p>
              </div>
            </div>
          </li>
        ))}
      </ol>

      <section className={`${SURFACE} mt-12 sm:mt-16 p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6`}>
        <div className="max-w-md">
          <h2 className={TEXT.h3}>Try it with something harmless</h2>
          <p className={`${TEXT.small} mt-1.5`}>
            Paste any text, create a link, open it in a private window. Watch it die on the second try.
          </p>
        </div>
        <Button size="lg" onClick={onNavigateHome} className="shrink-0">
          <Lock className="h-4 w-4" aria-hidden="true" />
          Share a secret
        </Button>
      </section>
    </div>
  );
}
